/**
 * Getting into a space: the GUI buys a one-time ticket with its session, then connects to
 * `/ws/<space>?ticket=<ticket>` on the space's server.
 *
 * The session is this browser's sign-in on that server (`accounts.ts`); the server lets in the
 * accounts of the space's team, each with their team role. This file only works out addresses.
 *
 * The space is either the page's own (a page served at `/s/<space>` by the server), or a
 * **remote** one: a GUI with its own core (the desktop app, the standalone build) opened with
 * `?remote=<space link>` talks to that server's space instead, while its own core keeps its
 * offline board for when it comes back ({@link remoteLink}).
 */
import { SpaceRefusal, spaceOf } from "./client";

/** A space's address. */
export interface SpaceLink {
  /** The server, `http(s)://host[:port]`. */
  server: string;
  /** The space's id. */
  space: string;
}

/** A team invite's address (`http(s)://host/join/<code>`). */
export interface InviteLink {
  /** The server. */
  server: string;
  /** The invite code. */
  code: string;
}

/** Thrown when the GUI cannot get in: the message says why, for people. */
export { SpaceRefusal };

/** A URL from `text`, if it is an `http(s)` address. */
function webUrl(text: string): URL | null {
  try {
    const url = new URL(text.trim());
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** Reads a space link (`http(s)://host/s/<space>`), or `null` if it is not one. */
export function parseSpaceLink(text: string): SpaceLink | null {
  const url = webUrl(text);
  const space = url && spaceOf(url.pathname);
  return url && space ? { server: url.origin, space } : null;
}

/** The invite code in a path `/join/<code>`, if it is one. */
export function inviteOf(pathname: string): string | null {
  const match = /^\/join\/([A-Za-z0-9]+)\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

/** Reads an invite link (`http(s)://host/join/<code>`), or `null` if it is not one. */
export function parseInviteLink(text: string): InviteLink | null {
  const url = webUrl(text);
  const code = url && inviteOf(url.pathname);
  return url && code ? { server: url.origin, code } : null;
}

/** The text of an invite link. */
export function inviteText(link: InviteLink): string {
  return `${link.server}/join/${link.code}`;
}

/** The remote space a GUI was opened with (`?remote=<link>` in its own address), if any. */
export function remoteLink(search: string): SpaceLink | null {
  const remote = new URLSearchParams(search).get("remote");
  return remote ? parseSpaceLink(remote) : null;
}

/** The text of a space link. */
export function linkText(link: SpaceLink): string {
  return `${link.server}/s/${link.space}`;
}

/** The space of a page served by a server at `/s/<space>`, if it is one. */
export function pageSpace(location: Pick<Location, "origin" | "pathname">): SpaceLink | null {
  const space = spaceOf(location.pathname);
  return space ? { server: location.origin, space } : null;
}

/**
 * The WebSocket address of `link`'s space with a one-time `ticket` (got with the session from
 * `/api/spaces/<space>/ticket`, so the session token itself never sits in an address, where
 * proxies and logs would keep it). Throws a {@link SpaceRefusal} without one: a space is only
 * for signed-in members of its team. `socket`: the WebSocket the ticket's answer named, when the
 * space lives on another server of the same cluster (the page stays where it is).
 */
export function spaceSocketUrl(link: SpaceLink, ticket: string | undefined, socket?: string): string {
  if (!ticket) throw new SpaceRefusal("sign in to open this space");
  if (socket && /^wss?:\/\//.test(socket)) return `${socket}?ticket=${encodeURIComponent(ticket)}`;
  const url = new URL(link.server);
  const scheme = url.protocol === "https:" ? "wss" : "ws";
  return `${scheme}://${url.host}/ws/${encodeURIComponent(link.space)}?ticket=${encodeURIComponent(ticket)}`;
}

/** The WebSocket address of the server's own core, for a page it served. */
export function ownSocketUrl(location: Pick<Location, "protocol" | "host">): string {
  return `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
}
